import * as DsIcon from "@equipmentshare/ds-icon";
import { pipe, ReadonlyArray } from "fp";
import React from "react";
import { CSVLink } from "react-csv";

import { Button } from "~/ButtonV2";
import * as Data from "~/Data";
import * as Driver from "~/Driver";
import * as Job from "~/Job";
import * as Log from "~/Log";
import { useAllFromActiveLog } from "~/Log/Event";
import { Time } from "~/Time";
import * as TimeTracking from "~/TimeTracking";
import * as WorkCode from "~/WorkCode";
import { WorkOrder } from "~/WorkOrder";

type DownloadProps = {
  readonly route: Log.Route;
  readonly onComplete: () => void;
};

/** A log event, as far as attributing work to it is concerned. */
type WorkBearing = Time.Interval & {
  readonly status?: Driver.Status.Status;
};

/**
 * The time entry whose work belongs on `event`, or `undefined` when the event
 * has no stopped interval, no status, or a status that cannot carry work.
 *
 * Both conditions live in `TimeTracking.Entry.fromInterval`, which takes a
 * `Period` — an interval *and* the status that says whether it could have
 * produced work. All this adds is the narrowing the export needs: an event with
 * no stopped interval, or no status at all, has no work to attribute.
 *
 * Exported for testing rather than inlined: this pairing is where the export
 * has gone wrong twice. It called the curried predicate without applying it
 * (`isWithinInterval(event) && entry`), which is a truthy function, so the
 * filter matched every entry and every exported row got whichever entry sorted
 * first. Rendering `Download` needs an available log, three preloaded stores
 * and a CSVLink whose blob URL is mocked away, so the pairing is unit-tested
 * here instead.
 */
export const toEntry = (store: Data.Store.Store<TimeTracking.Entry.Entry>) => (
  event: WorkBearing
): TimeTracking.Entry.Entry | undefined =>
  pipe(
    store,
    !event.interval || !Time.Interval.isStopped(event) || !event.status
      ? () => undefined
      : TimeTracking.Entry.fromInterval({ ...event, status: event.status })
  );

/**
 * The job name for `entry`, or `""` when it has none or the job list has not
 * loaded yet.
 *
 * Exported for testing, and a thin wrapper for one reason: `Job.asDescription`
 * takes a *job* reference and matches it by id, whereas its counterpart
 * `WorkCode.asDescription` takes the whole `Entry`. This call site passed the
 * `Entry` to both, so the job lookup compared a job id against a time-entry id,
 * matched nothing, and left the column empty on every export. Both are
 * `{ id: string }`, so it type-checked.
 */
export const toJobDescription = (jobs: readonly Job.Job[] | undefined) => (
  entry?: TimeTracking.Entry.Entry
): string => pipe(jobs, Job.asDescription(entry?.job));

export function Download(props: DownloadProps) {
  const TimeTrackingStore = TimeTracking.Entry.useStore();
  const workCodeStore = WorkCode.useStore();
  const jobs = Job.useWhenAvailable();
  return pipe(
    useAllFromActiveLog(props.route),
    ReadonlyArray.map((event) => ({
      event,
      entry: toEntry(TimeTrackingStore)(event),
    })),
    ReadonlyArray.map((data) => ({
      status: Driver.Status.toString(data.event.status),
      time: Time.Interval.toHumanized(data.event),
      duration: Time.Duration.toEnglish(
        Time.Interval.Stopped.toDuration(
          Time.Interval.when(data.event, {
            started: Time.Interval.Started.toStopped(Time.DateTime.now()),
            stopped: (stopped) => stopped,
          })
        )
      ),
      location: data.event.location,
      source: Log.Event.Source.toDescription(data.event.sources.current),
      workOrder:
        data.entry?.workOrder &&
        WorkOrder.toString({ workOrder: data.entry.workOrder }),
      workCode: pipe(workCodeStore, WorkCode.asDescription(data.entry)),
      job: toJobDescription(jobs)(data.entry),
      notes: data.event.notes,
    })),
    ([first, ...rest]) =>
      !first ? (
        <DownloadButton disabled />
      ) : (
        <CSVLink
          target="_blank"
          style={{ paddingTop: 0, paddingBottom: 0 }}
          data={[first, ...rest]}
          filename={`duty-status-export-${Time.DateTime.toISOWithoutTime(
            Time.DateTime.now()
          )}${
            props.route.driver?.name
              ? `-${Driver.Name.toString(props.route.driver.name)}`
              : ""
          }.csv`}
        >
          <DownloadButton onClick={props.onComplete} />
        </CSVLink>
      )
  );
}

export function DownloadButton(props: {
  readonly disabled?: boolean;
  readonly onClick?: () => void;
}) {
  return (
    <Button
      icon={<DsIcon.Icon name="$icon_download_line" size="$icon_size_4" />}
      type="text"
      style={{ padding: 0 }}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      &nbsp;Download CSV
    </Button>
  );
}
